import { useState, useRef, type FunctionComponent, type KeyboardEvent } from 'react'
import {
  Modal,
  ModalContainer,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@retailer-platform/shared-components'
import {
  Text,
  Button,
  Flex,
  SearchInput,
  CloseButton,
  spacing,
} from '@retailer-platform/shared-components/src/tds'
import { useDomainMessages } from '../utils/domain/intl'

interface FastFindModalProps {
  onClose: () => void
  onSearch: (query: string) => void
}

const FastFindModal: FunctionComponent<FastFindModalProps> = ({ onClose, onSearch }) => {
  const [query, setQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const messages = useDomainMessages({
    title: 'customersAndOrdersDomain.fastFind.title',
    searchLabel: 'customersAndOrdersDomain.fastFind.searchLabel',
    placeholder: 'customersAndOrdersDomain.fastFind.placeholder',
    partialSearchWarning: 'customersAndOrdersDomain.fastFind.partialSearchWarning',
    cancel: 'customersAndOrdersDomain.fastFind.cancel',
    search: 'customersAndOrdersDomain.fastFind.search',
    close: 'customersAndOrdersDomain.fastFind.close',
  })

  const handleClose = () => {
    onClose()
  }

  const handleSearch = () => {
    const trimmed = query.trim()
    if (trimmed) {
      onSearch(trimmed)
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  return (
    <Modal
      isOpen
      onRequestClose={handleClose}
      onAfterOpen={() => inputRef.current?.focus()}
      maxWidth={500}
      hideCloseButton
      styleOverrides={{ overlay: { zIndex: 600 } }}
    >
      <ModalContainer>
        <ModalHeader
          css={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingLeft: spacing.s24,
            paddingRight: spacing.s24,
          }}
        >
          <Text typography="title" css={{ fontWeight: 700 }}>
            {messages.title}
          </Text>
          <CloseButton onClick={handleClose} variant="secondary" aria-label={messages.close} />
        </ModalHeader>

        <ModalBody
          css={{
            paddingLeft: spacing.s24,
            paddingRight: spacing.s24,
            paddingBottom: spacing.s24,
            display: 'flex',
            flexDirection: 'column',
            gap: spacing.s16,
          }}
        >
          <SearchInput
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            labelProps={{ label: messages.searchLabel }}
            placeholderText={messages.placeholder}
            onClear={() => setQuery('')}
            width="full"
          />
          <Text typography="bodyRegular">{messages.partialSearchWarning}</Text>
        </ModalBody>

        <ModalFooter>
          <Flex.Row justifyContent="flex-end" gap={spacing.s16} alignItems="center">
            <Button variant="tertiary" onClick={handleClose}>
              {messages.cancel}
            </Button>
            <Button variant="primary" onClick={handleSearch} disabled={!query.trim()}>
              {messages.search}
            </Button>
          </Flex.Row>
        </ModalFooter>
      </ModalContainer>
    </Modal>
  )
}

export default FastFindModal
